/**
 * ============================================================================
 * EL-ARO
 * COMPONENT: Mobile Navigation
 * FILE: assets/elaro-mobile-navigation.js
 * ============================================================================
 */

class ElaroMobileNavigation {

    constructor(root) {

        this.root = root;

        this.viewport = root.querySelector(
            "[data-navigation-viewport]"
        );

        this.panels = Array.from(
            root.querySelectorAll(
                "[data-navigation-panel]"
            )
        );

        this.drawer = root.closest(
            "[data-mobile-drawer]"
        );

        this.title = this.drawer
            ? this.drawer.querySelector(
                "[data-mobile-drawer-title]"
            )
            : null;

        this.backButton = this.drawer
            ? this.drawer.querySelector(
                "[data-mobile-drawer-back]"
            )
            : null;

        this.history = ["root"];

        if (!this.viewport || !this.panels.length) {

            return;

        }

        this.bindEvents();

        this.reset();

    }


    /*==========================================================================
      EVENTS
    ==========================================================================*/

    bindEvents() {

        this.root.addEventListener(
            "click",
            this.handleClick.bind(this)
        );

        if (this.backButton) {

            this.backButton.addEventListener(
                "click",
                event => {

                    event.preventDefault();

                    this.goBack();

                }
            );

        }

    }


    /*==========================================================================
      CLICK HANDLER
    ==========================================================================*/

    handleClick(event) {

        const trigger = event.target.closest(
            "[data-navigation-open]"
        );

        if (!trigger || !this.root.contains(trigger)) {

            return;

        }

        event.preventDefault();

        const target = trigger.dataset.navigationTarget;

        const title = trigger.dataset.navigationTitle
            || trigger.querySelector(
                ".elaro-mobile-navigation__label"
            )?.textContent.trim()
            || "";

        if (!target) {

            return;

        }

        this.openPanel(
            target,
            title
        );

    }


    /*==========================================================================
      OPEN PANEL
    ==========================================================================*/

    openPanel(panelId, title) {

        const nextPanel = this.getPanel(
            panelId
        );

        if (!nextPanel) {

            console.warn(
                "[EL-ARO] Mobile navigation panel not found:",
                panelId
            );

            return;

        }

        const currentId =
            this.history[
                this.history.length - 1
            ];

        if (currentId === panelId) {

            return;

        }

        const currentPanel = this.getPanel(
            currentId
        );

        this.history.push(
            panelId
        );

        /*
         * Current panel moves left.
         */
        if (currentPanel) {

            currentPanel.classList.remove(
                "is-active"
            );

            currentPanel.classList.add(
                "is-previous"
            );

        }

        /*
         * Target panel enters.
         */
        nextPanel.classList.remove(
            "is-previous"
        );

        nextPanel.classList.add(
            "is-active"
        );

        this.updateHeader(
            title
        );

    }


    /*==========================================================================
      BACK
    ==========================================================================*/

    goBack() {

        if (this.history.length <= 1) {

            return;

        }

        const currentId =
            this.history.pop();

        const previousId =
            this.history[
                this.history.length - 1
            ];

        const currentPanel =
            this.getPanel(currentId);

        const previousPanel =
            this.getPanel(previousId);

        /*
         * Current child moves right.
         */
        if (currentPanel) {

            currentPanel.classList.remove(
                "is-active"
            );

            currentPanel.classList.remove(
                "is-previous"
            );

        }

        /*
         * Previous panel returns.
         */
        if (previousPanel) {

            previousPanel.classList.remove(
                "is-previous"
            );

            previousPanel.classList.add(
                "is-active"
            );

        }

        const previousTitle =
            previousPanel
                ? previousPanel.dataset.navigationTitle
                : "Menu";

        this.updateHeader(
            previousTitle
        );

    }


    /*==========================================================================
      RESET
    ==========================================================================*/

    reset() {

        this.history = ["root"];

        this.panels.forEach(
            panel => {

                panel.classList.remove(
                    "is-active",
                    "is-previous"
                );

            }
        );

        const rootPanel =
            this.getPanel("root");

        if (rootPanel) {

            rootPanel.classList.add(
                "is-active"
            );

        }

        this.updateHeader(
            rootPanel
                ? rootPanel.dataset.navigationTitle
                : "Menu"
        );

    }


    /*==========================================================================
      HEADER
    ==========================================================================*/

    updateHeader(title) {

        if (this.title) {

            this.title.textContent =
                title || "Menu";

        }

        if (this.backButton) {

            this.backButton.hidden =
                this.history.length <= 1;

        }

    }


    /*==========================================================================
      PANEL LOOKUP
    ==========================================================================*/

    getPanel(panelId) {

        if (!panelId) {

            return null;

        }

        return this.panels.find(
            panel =>
                panel.dataset.navigationPanelId === panelId
        ) || null;

    }

}


/*==============================================================================
  MANAGER
==============================================================================*/

class ElaroMobileNavigationManager {

    initialize() {

        document
            .querySelectorAll(
                "[data-navigation]"
            )
            .forEach(root => {

                if (
                    root.dataset.navigationInitialized === "true"
                ) {

                    return;

                }

                new ElaroMobileNavigation(
                    root
                );

                root.dataset.navigationInitialized =
                    "true";

            });

    }

}


/*==============================================================================
  INITIALIZE
==============================================================================*/

const initializeMobileNavigation = () => {

    new ElaroMobileNavigationManager()
        .initialize();

};


document.addEventListener(
    "DOMContentLoaded",
    initializeMobileNavigation
);


document.addEventListener(
    "shopify:section:load",
    initializeMobileNavigation
);